/**
 * SellerBillingBanner — top-of-dashboard nudge that surfaces the
 * seller's SaaS subscription state when it needs attention.
 *
 * Shows up in 4 cases:
 *   • No plan picked yet     → emerald "Pick a plan to activate verqo"
 *   • Grace period           → amber  "Wallet short — top up in N days"
 *   • Read-only mode         → rose   "Account read-only — restore now"
 *   • Suspended              → rose   "Account suspended — reactivate"
 *
 * Hidden when status === "active" (zero noise on the happy path).
 * Owner-only — non-owner team roles see nothing (they can't fix it).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { AlertTriangle, Sparkles, Lock, Clock, ArrowUpRight } from "lucide-react";
import { Button } from "./ui/button";


const BANNER_BY_STATUS = {
  unsubscribed: {
    tone:   "emerald",
    icon:   Sparkles,
    title:  "Activate your verqo plan",
    body:   "Pick a plan to unlock invoicing, payouts, and buyer credit. First month deducts from your wallet.",
    cta:    "Choose a plan",
    testid: "billing-banner-unsubscribed",
  },
  grace: {
    tone:   "amber",
    icon:   Clock,
    title:  "Your verqo wallet was short — top up to avoid interruption",
    body:   (s) => `We couldn't charge your subscription. You have until ${fmtDate(s.grace_until)} to top up before your account flips to read-only.`,
    cta:    "Top up + review",
    testid: "billing-banner-grace",
  },
  read_only: {
    tone:   "rose",
    icon:   Lock,
    title:  "Account is read-only — top up to restore",
    body:   "You can still receive payments, but new invoices, new buyers, and outbound flows are blocked until we charge the next cycle.",
    cta:    "Restore access",
    testid: "billing-banner-read-only",
  },
  suspended: {
    tone:   "rose",
    icon:   AlertTriangle,
    title:  "Account suspended — reactivate to continue",
    body:   "Your buyers can no longer access your storefront. Reactivate by paying the outstanding balance from your wallet.",
    cta:    "Reactivate",
    testid: "billing-banner-suspended",
  },
};

const TONE_CLASSES = {
  emerald: {
    bg:     "border-emerald-300 bg-emerald-50",
    icon:   "bg-emerald-200 text-emerald-800",
    title:  "text-emerald-900",
    btn:    "bg-emerald-700 text-emerald-50 hover:bg-emerald-700/90",
    label:  "text-emerald-900/70",
  },
  amber: {
    bg:     "border-amber-300 bg-amber-50",
    icon:   "bg-amber-200 text-amber-800",
    title:  "text-amber-900",
    btn:    "bg-amber-900 text-amber-50 hover:bg-amber-900/90",
    label:  "text-amber-900/70",
  },
  rose: {
    bg:     "border-rose-300 bg-rose-50",
    icon:   "bg-rose-200 text-rose-800",
    title:  "text-rose-900",
    btn:    "bg-rose-700 text-rose-50 hover:bg-rose-700/90",
    label:  "text-rose-900/70",
  },
};


function fmtDate(iso) {
  if (!iso) return "the grace period";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
}


export default function SellerBillingBanner({ user }) {
  const [status, setStatus] = useState(null);

  // Only the seller owner sees the billing banner.
  const isOwner = user && user.role === "seller" && (user.team_role || "owner") === "owner";

  useEffect(() => {
    if (!isOwner) return;
    api.get("/billing/status")
       .then(r => setStatus(r.data))
       .catch(() => {});
  }, [isOwner]);

  if (!isOwner || !status) return null;
  // Master kill switch — pre-launch the banner stays hidden so sellers
  // don't see "Choose a plan" CTAs that wouldn't do anything yet.
  if (status.enforced === false) return null;
  const cfg = BANNER_BY_STATUS[status.status];
  if (!cfg) return null;   // active = no banner

  const tone = TONE_CLASSES[cfg.tone];
  const Icon = cfg.icon;
  const body = typeof cfg.body === "function" ? cfg.body(status) : cfg.body;

  return (
    <div className={`rounded-2xl border p-5 flex items-start gap-4 ${tone.bg}`} data-testid={cfg.testid}>
      <div className={`w-10 h-10 rounded-full grid place-items-center shrink-0 ${tone.icon}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1">
        <div className={`text-xs uppercase tracking-widest font-medium ${tone.label}`}>verqo subscription</div>
        <div className={`font-medium mt-1 ${tone.title}`}>{cfg.title}</div>
        <p className={`text-[13px] mt-1 ${tone.label}`}>{body}</p>
      </div>
      <Link to="/seller/settings#plan-billing">
        <Button className={`rounded-full ${tone.btn}`} data-testid="billing-banner-cta">
          {cfg.cta}<ArrowUpRight className="w-3 h-3 ml-1" />
        </Button>
      </Link>
    </div>
  );
}
